
import React from "react";
import { cn } from "@/lib/utils";
import { ChevronRight } from "lucide-react";
import { 
  Accordion,
  AccordionContent,
  AccordionItem,
  AccordionTrigger,
} from "@/components/ui/accordion";
import { GuideSection } from "@/types/guideTypes";

interface GuideContentProps {
  filteredContent: GuideSection[];
  activeSection: string;
  expandedSections: string[];
  searchQuery: string;
}

export const GuideContent: React.FC<GuideContentProps> = ({
  filteredContent,
  activeSection,
  expandedSections,
  searchQuery
}) => {
  return (
    <div className="flex-1 overflow-auto">
      <div className="max-w-4xl mx-auto p-8">
        {filteredContent.map((section) => (
          <div
            key={section.id}
            id={section.id}
            className={cn(
              "mb-12",
              activeSection !== section.id && !searchQuery && "hidden"
            )}
          >
            <div className="flex items-center gap-3 mb-6">
              <section.icon className="h-8 w-8 text-primary" />
              <div>
                <h2 className="text-2xl font-bold">{section.title}</h2>
                <div className="flex items-center gap-2 mt-1">
                  <span className={cn(
                    "text-xs font-medium px-2 py-1 rounded",
                    section.difficulty === "Beginner" && "bg-green-100 text-green-700",
                    section.difficulty === "Intermediate" && "bg-blue-100 text-blue-700",
                    section.difficulty === "Advanced" && "bg-purple-100 text-purple-700"
                  )}>
                    {section.difficulty}
                  </span>
                </div>
              </div>
            </div>

            {section.content.map((text, idx) => (
              <p key={idx} className="text-gray-600 mb-4 text-lg">
                {text}
              </p>
            ))}

            <Accordion
              type="multiple"
              value={expandedSections}
              className="mt-6"
            >
              {section.subsections?.map((subsection, idx) => (
                <AccordionItem key={idx} value={`${section.id}-${idx}`}>
                  <AccordionTrigger className="text-lg font-semibold">
                    {subsection.title}
                  </AccordionTrigger>
                  <AccordionContent>
                    <div className="space-y-3 pl-4">
                      {subsection.content.map((text, contentIdx) => (
                        <div key={contentIdx} className="flex items-start gap-2">
                          <ChevronRight className="h-5 w-5 text-primary mt-0.5 flex-shrink-0" />
                          <p className="text-gray-700">{text}</p>
                        </div>
                      ))}
                    </div>
                  </AccordionContent>
                </AccordionItem>
              ))}
            </Accordion>
          </div>
        ))}
      </div>
    </div>
  );
};
